import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import {
  Users, Phone, PhoneCall, Clock, CalendarCheck, AlertTriangle, Target, Trophy, Filter,
  ArrowRight, RefreshCw, MessageCircle, MoreHorizontal, Zap, BarChart3, CalendarDays,
  CheckCircle2, TrendingUp, TrendingDown, AlarmClock, FileText, PhoneOff, Sparkles
} from 'lucide-react';
import { dashboardAPI } from '../api';
import { useAuth } from '../context/AuthContext';
import { leadNo, prioBadge } from '../lib/leadDisplay';
import ClickToCallButton from '../components/ClickToCallButton';

const PERIODS = [['today', 'Today'], ['week', 'This Week'], ['month', 'This Month']];
const TABS = [
  ['priority', 'Priority'], ['fresh', 'Fresh'], ['callback', 'Callback'],
  ['hot', 'Hot'], ['overdue', 'Overdue'], ['untouched', 'Untouched'],
];

const greet = () => {
  const h = new Date().getHours();
  return h < 12 ? 'Good Morning' : h < 17 ? 'Good Afternoon' : 'Good Evening';
};
const inr = (v) => {
  const n = Number(v || 0);
  if (n >= 10000000) return `₹${(n / 10000000).toFixed(1)}Cr`;
  if (n >= 100000) return `₹${(n / 100000).toFixed(1)}L`;
  if (n >= 1000) return `₹${(n / 1000).toFixed(1)}K`;
  return `₹${n.toLocaleString('en-IN')}`;
};
const rupee = (v) => `₹${Number(v || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;
const clock = (v) => (v ? new Date(v).toLocaleTimeString('en-IN', {
  timeZone: 'Asia/Kolkata', hour: '2-digit', minute: '2-digit', hour12: true,
}) : '—');
const dayLabel = (v) => {
  if (!v) return '—';
  const d = new Date(v);
  const now = new Date();
  const same = d.toDateString() === now.toDateString();
  const yday = new Date(now.getTime() - 86400000).toDateString() === d.toDateString();
  const days = Math.floor((now - d) / 86400000);
  if (same) return `Today, ${clock(v)}`;
  if (yday) return `Yesterday, ${clock(v)}`;
  return `${days} days ago`;
};
const dueLabel = (row) => {
  if (row.is_overdue) return 'Overdue';
  if (row.due_in_seconds == null) return '—';
  const m = Math.round(row.due_in_seconds / 60);
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  return `${h}h ${m % 60}m`;
};

const OUTCOME_TONE = {
  Connected: 'text-emerald-600',
  'No Answer': 'text-slate-500',
  Missed: 'text-slate-500',
  Busy: 'text-amber-600',
  Rejected: 'text-rose-600',
  Cancelled: 'text-slate-400',
  'Not Called': 'text-blue-600',
};
const PIPE_TONE = [
  'bg-[#EAF2FE] text-[#1B63D8]', 'bg-[#E9F8EF] text-[#127A43]', 'bg-[#F1EDFD] text-[#6A3FD1]',
  'bg-[#FDEEF3] text-[#C2185B]', 'bg-[#FEF6E7] text-[#B7761A]', 'bg-[#EAF2FE] text-[#1B63D8]',
  'bg-[#F1EDFD] text-[#6A3FD1]', 'bg-[#E9F8EF] text-[#127A43]',
];

const Panel = ({ title, icon: Icon, iconTone = 'text-blue-600', action, onAction,
  actionLabel = 'View All', right, children, testid, className = '' }) => (
  <section data-testid={testid}
    className={`clay-card flex h-full min-w-0 flex-col p-4 ${className}`}>
    <header className="mb-3 flex flex-wrap items-center justify-between gap-x-3 gap-y-2">
      <h2 className="flex min-w-0 items-center gap-2 font-outfit text-base font-bold text-slate-900 dark:text-slate-100">
        {Icon && <Icon className={`h-[18px] w-[18px] shrink-0 ${iconTone}`} />}
        <span className="truncate">{title}</span>
      </h2>
      <div className="flex min-w-0 max-w-full items-center gap-2">
        {right}
        {action && (
          <button data-testid={`${testid}-action`} onClick={onAction}
            className="inline-flex shrink-0 items-center gap-1 text-xs font-semibold text-blue-600 transition-colors hover:text-blue-800">
            {actionLabel}<ArrowRight className="h-3 w-3" />
          </button>
        )}
      </div>
    </header>
    <div className="min-w-0 flex-1">{children}</div>
  </section>
);

const Bar = ({ pct, tone = 'bg-blue-500' }) => (
  <span className="mt-2 block h-1.5 w-full overflow-hidden rounded-full bg-white/70">
    <span className={`block h-full rounded-full transition-all duration-700 ${tone}`}
      style={{ width: `${Math.max(2, Math.min(pct || 0, 100))}%` }} />
  </span>
);

const KpiCard = ({ card, loading, onClick }) => {
  const { key, label, value, sub, subTone, Icon, tone, pct, barTone, delta } = card;
  return (
    <button type="button" data-testid={`se-kpi-${key}`} onClick={onClick}
      className={`group flex min-w-0 flex-col justify-between rounded-xl border p-2.5 text-left transition-all duration-200 hover:-translate-y-0.5 hover:shadow-[0_8px_30px_rgb(0_0_0/0.04)] sm:p-3.5 ${tone.wrap}`}>
      <span className="flex items-start gap-2 sm:gap-3">
        <span className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full sm:h-11 sm:w-11 ${tone.icon}`}>
          <Icon className="h-4 w-4 sm:h-5 sm:w-5" />
        </span>
        <span className="min-w-0 flex-1">
          <span className="block truncate text-[11px] font-semibold text-slate-600 dark:text-slate-300 sm:text-[13px]">{label}</span>
          <span className="mt-0.5 flex items-baseline gap-1.5">
            <span data-testid={`se-kpi-${key}-value`}
              className={`font-outfit text-[20px] font-extrabold leading-none sm:text-[26px] ${tone.value}`}>
              {loading ? '—' : value}
            </span>
            {delta != null && !loading && (
              <span className={`flex items-center gap-0.5 text-[10px] font-bold sm:text-[11px] ${delta >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>
                {delta >= 0 ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}
                {Math.abs(delta)}%
              </span>
            )}
          </span>
        </span>
      </span>
      {pct != null && <Bar pct={pct} tone={barTone} />}
      {sub && (
        <span data-testid={`se-kpi-${key}-sub`}
          className={`mt-1.5 block truncate text-[10px] font-medium sm:mt-2 sm:text-[11px] ${subTone || 'text-slate-500 dark:text-slate-400'}`}>
          {sub}
        </span>
      )}
    </button>
  );
};

const Donut = ({ pct }) => {
  const r = 46;
  const c = 2 * Math.PI * r;
  const filled = (Math.min(pct || 0, 100) / 100) * c;
  return (
    <div className="relative h-[118px] w-[118px] shrink-0" data-testid="se-target-donut">
      <svg viewBox="0 0 110 110" className="h-full w-full -rotate-90">
        <circle cx="55" cy="55" r={r} fill="none" stroke="#EDF1F6" strokeWidth="11" />
        <circle cx="55" cy="55" r={r} fill="none" stroke="#16A34A" strokeWidth="11" strokeLinecap="round"
          strokeDasharray={`${filled} ${c - filled}`} className="transition-all duration-700" />
      </svg>
      <span className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="font-outfit text-[26px] font-extrabold leading-none text-slate-900 dark:text-white">{pct || 0}%</span>
        <span className="mt-0.5 text-[10px] font-medium text-slate-500">Overall Progress</span>
      </span>
    </div>
  );
};

const TARGET_BARS = ['bg-blue-500', 'bg-emerald-500', 'bg-violet-500'];

export const SalesExecutiveDashboardPage = () => {
  const navigate = useNavigate();
  const { userId } = useParams();
  // /team/:userId/dashboard — read-only view of a mapped team member. The backend rejects
  // any id that is not in the caller's canonical team, so this is never a client-side gate.
  const readOnly = !!userId;
  const { user, hasPermission } = useAuth();
  // The Activity Log route requires activity.view; never offer a link that would 403.
  const canViewActivityLog = !!hasPermission?.('activity.view');
  const [period, setPeriod] = useState('today');
  const [tab, setTab] = useState('priority');
  const [filters, setFilters] = useState({ service: '', source: '', priority: '' });
  const [showFilters, setShowFilters] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const inflight = useRef('');

  // ONE aggregated request per period. Guarded so React StrictMode's double effect
  // invocation (and re-renders) can never duplicate the call.
  const load = useCallback(async (p, force = false) => {
    const key = `${p}|${userId || ''}`;
    if (!force && inflight.current === key) return;
    inflight.current = key;
    setLoading(true);
    try {
      const res = await dashboardAPI.getSalesExecutive(p, userId);
      setData(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not load your dashboard');
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => { load(period); }, [period, load]);

  const K = data?.kpis || {};
  const rows = data?.queue?.rows || [];
  const counts = data?.queue?.counts || {};

  const serviceOpts = useMemo(() => [...new Set(rows.map((r) => r.service).filter((s) => s && s !== '—'))], [rows]);
  const sourceOpts = useMemo(() => [...new Set(rows.map((r) => r.source).filter((s) => s && s !== '—'))], [rows]);

  // Tabs + filters are pure client-side views of the already-loaded rows: zero extra requests.
  const visibleRows = useMemo(() => rows.filter((r) => {
    if (tab !== 'priority' && !r.flags?.[tab]) return false;
    if (filters.service && r.service !== filters.service) return false;
    if (filters.source && r.source !== filters.source) return false;
    if (filters.priority && r.priority !== filters.priority) return false;
    return true;
  }), [rows, tab, filters]);

  const kpiCards = [
    {
      key: 'new-leads', label: 'New Leads', value: K.new_leads?.value ?? 0, Icon: Users,
      delta: K.new_leads?.delta_pct,
      sub: `${K.new_leads?.contacted ?? 0} contacted | ${K.new_leads?.untouched ?? 0} untouched`,
      tone: { wrap: 'border-[#D5E4FC] bg-[#EAF2FE] dark:bg-[#111C2E]', icon: 'bg-[#D3E3FD] text-[#1B63D8]', value: 'text-slate-900 dark:text-white' },
      path: '/leads?filter=new',
    },
    {
      key: 'calls-dialed', label: 'Calls Dialed', Icon: Phone,
      value: K.calls_dialed?.target ? `${K.calls_dialed?.value ?? 0} / ${K.calls_dialed.target}` : (K.calls_dialed?.value ?? 0),
      pct: K.calls_dialed?.pct ?? null, barTone: 'bg-emerald-500',
      sub: K.calls_dialed?.remaining != null ? `${K.calls_dialed.remaining} calls remaining` : 'No call target set',
      tone: { wrap: 'border-[#CFEEDC] bg-[#E9F8EF] dark:bg-[#0F2019]', icon: 'bg-[#CDEEDA] text-[#127A43]', value: 'text-slate-900 dark:text-white' },
      path: '/my-calls',
    },
    {
      key: 'connected-calls', label: 'Connected Calls', value: K.connected_calls?.value ?? 0, Icon: Clock,
      pct: K.connected_calls?.connect_rate ?? 0, barTone: 'bg-violet-500',
      sub: `${K.connected_calls?.connect_rate ?? 0}% connect rate`,
      tone: { wrap: 'border-[#E1D9FB] bg-[#F1EDFD] dark:bg-[#171429]', icon: 'bg-[#E1D9FB] text-[#6A3FD1]', value: 'text-slate-900 dark:text-white' },
      path: '/my-calls',
    },
    {
      key: 'qualified', label: 'Qualified Sales', Icon: Users,
      value: K.qualified?.target ? `${K.qualified?.value ?? 0} / ${K.qualified.target}` : (K.qualified?.value ?? 0),
      pct: K.qualified?.pct ?? null, barTone: 'bg-amber-500',
      sub: K.qualified?.remaining != null ? `${K.qualified.remaining} more to go` : 'No sales target set',
      tone: { wrap: 'border-[#FBE7C2] bg-[#FEF6E7] dark:bg-[#231C0F]', icon: 'bg-[#FBE7C2] text-[#B7761A]', value: 'text-slate-900 dark:text-white' },
      path: '/pipeline',
    },
    {
      key: 'followups-due', label: 'Follow-ups Due', value: K.followups_due?.value ?? 0, Icon: CalendarCheck,
      sub: `${K.followups_due?.today ?? 0} today`,
      tone: { wrap: 'border-[#FAD9E4] bg-[#FDEEF3] dark:bg-[#26141B]', icon: 'bg-[#FAD9E4] text-[#C2185B]', value: 'text-slate-900 dark:text-white' },
      path: '/work-queue',
    },
    {
      key: 'overdue', label: 'Overdue', value: K.overdue?.value ?? 0, Icon: AlertTriangle,
      sub: (K.overdue?.value ?? 0) > 0 ? 'Take action now' : 'All clear',
      subTone: (K.overdue?.value ?? 0) > 0 ? 'text-rose-600 font-semibold' : 'text-emerald-600 font-semibold',
      tone: { wrap: 'border-[#FAD5D5] bg-[#FDECEC] dark:bg-[#271416]', icon: 'bg-[#FAD5D5] text-[#D32F2F]', value: 'text-rose-600' },
      path: '/work-queue',
    },
  ];

  const T = data?.target || {};
  const F = data?.fresh_response || {};
  const P = data?.performance || {};
  const I = data?.incentive || {};
  const untouchedAlert = counts.untouched || 0;
  const oldestUntouched = data?.queue?.untouched_oldest_days || 0;
  const periodLabel = (PERIODS.find(([k]) => k === period) || [null, 'Today'])[1];

  const perfCells = [
    ['contact-rate', 'Contact Rate', `${P.contact_rate ?? 0}%`, P.contact_rate_delta, 'text-emerald-600'],
    ['qualification-rate', 'Qualification Rate', `${P.qualification_rate ?? 0}%`, P.qualification_rate_delta, 'text-amber-600'],
    ['conversion-rate', 'Conversion Rate', `${P.conversion_rate ?? 0}%`, P.conversion_rate_delta, 'text-violet-600'],
    ['avg-response', 'Avg. Response Time', P.avg_response_hms || '0s', P.avg_response_delta, 'text-slate-900 dark:text-white'],
  ];

  return (
    <div className="space-y-4" data-testid="sales-executive-dashboard">
      {/* greeting + period switch */}
      <section className="flex flex-wrap items-start justify-between gap-4" data-testid="se-header">
        <div className="min-w-0">
          {readOnly ? (
            <>
              <button onClick={() => navigate('/dashboard')} data-testid="se-back-to-team"
                className="mb-1 inline-flex items-center gap-1 text-xs font-bold text-blue-600 hover:text-blue-800">
                ← Back to Team Dashboard
              </button>
              <h1 className="flex flex-wrap items-center gap-2 font-outfit text-[26px] font-extrabold leading-tight text-slate-900 dark:text-white sm:text-[30px]"
                data-testid="se-readonly-title">
                {data?.user?.name || 'Team Member'}
                <span className="rounded-lg border border-amber-200 bg-amber-50 px-2 py-0.5 text-[11px] font-bold text-amber-700">Read-only view</span>
              </h1>
              <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">Live dashboard of your mapped team member.</p>
            </>
          ) : (
            <>
              <h1 className="flex items-center gap-2 font-outfit text-[28px] font-extrabold leading-tight text-slate-900 dark:text-white sm:text-[32px]">
                {greet()}, {(user?.name || '').split(' ')[0] || 'there'}!
                <Sparkles className="h-6 w-6 shrink-0 text-amber-500" />
              </h1>
              <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">Targets are dreams with deadlines. Keep going!</p>
            </>
          )}
        </div>
        <div className="flex items-center gap-3">
          <p className="hidden max-w-[240px] border-r border-amber-300/70 pr-3 text-right text-[13px] italic leading-snug text-slate-500 lg:block">
            <span className="mr-1 font-serif text-lg not-italic text-amber-500">“</span>
            Discipline today, bigger success tomorrow.
          </p>
          <div className="flex flex-wrap items-center gap-1.5" data-testid="se-period-switch">
            <span className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200"
              data-testid="se-date-chip">
              <CalendarDays className="h-3.5 w-3.5 text-slate-400" />
              {new Date().toLocaleDateString('en-IN', { month: 'short', year: 'numeric' })}
            </span>
            {PERIODS.map(([k, label]) => (
              <button key={k} data-testid={`se-period-${k}`} onClick={() => setPeriod(k)}
                className={`rounded-lg px-3.5 py-1.5 text-xs font-semibold transition-colors ${period === k
                  ? 'bg-[#0E1626] text-white shadow-sm'
                  : 'border border-slate-200 bg-white text-slate-600 hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-300'}`}>
                {label}
              </button>
            ))}
          </div>
          <button data-testid="se-refresh" onClick={() => load(period, true)} title="Refresh"
            className="rounded-xl border border-slate-200 bg-white p-2 text-slate-500 transition-colors hover:bg-slate-50 dark:border-slate-800 dark:bg-slate-900">
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </section>

      {/* KPI row */}
      <section className="grid grid-cols-2 gap-2.5 max-[359px]:grid-cols-1 sm:gap-3 lg:grid-cols-3 xl:grid-cols-6" data-testid="se-kpi-row">
        {kpiCards.map((c) => <KpiCard key={c.key} card={c} loading={loading} onClick={() => navigate(c.path)} />)}
      </section>

      {/* work queue + target/incentive */}
      <div className="grid grid-cols-1 items-start gap-4 xl:grid-cols-[minmax(0,2.15fr)_minmax(0,1fr)]">
        <div className="min-w-0 space-y-3">
          <Panel testid="se-work-queue" title="Priority Work Queue" icon={Zap} iconTone="text-blue-600"
            right={(
              <span className="-mx-1 flex min-w-0 max-w-full items-center gap-1.5 overflow-x-auto px-1 py-0.5 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
                {TABS.map(([k, label]) => (
                  <button key={k} data-testid={`se-tab-${k}`} onClick={() => setTab(k)}
                    className={`shrink-0 whitespace-nowrap rounded-lg px-2.5 py-1.5 text-[11px] font-bold transition-colors ${tab === k
                      ? 'bg-[#0E1626] text-white shadow-sm'
                      : k === 'overdue' ? 'border border-rose-200 bg-rose-50 text-rose-600 hover:bg-rose-100'
                        : k === 'hot' ? 'border border-amber-200 bg-amber-50 text-amber-700 hover:bg-amber-100'
                          : 'border border-slate-200 bg-white text-slate-600 hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-300'}`}>
                    {label} ({k === 'priority' ? (counts.priority ?? 0) : (counts[k] ?? 0)})
                  </button>
                ))}
                <button data-testid="se-filters-toggle" onClick={() => setShowFilters((s) => !s)}
                  className={`inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-lg border px-2.5 py-1.5 text-[11px] font-bold transition-colors ${showFilters
                    ? 'border-blue-200 bg-blue-50 text-blue-700' : 'border-slate-200 bg-white text-slate-600 hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-300'}`}>
                  <Filter className="h-3 w-3" />Filters
                </button>
              </span>
            )}>
            {showFilters && (
              <div className="mb-3 flex flex-wrap items-center gap-2 rounded-xl border border-slate-200 bg-slate-50/70 p-2.5 dark:border-slate-800 dark:bg-slate-800/40"
                data-testid="se-filters-panel">
                {[['service', 'All Services', serviceOpts], ['source', 'All Sources', sourceOpts],
                  ['priority', 'All Priorities', ['urgent', 'high', 'medium', 'low']]].map(([key, all, items]) => (
                    <select key={key} data-testid={`se-filter-${key}`} value={filters[key]}
                      onChange={(e) => setFilters({ ...filters, [key]: e.target.value })}
                      className="cursor-pointer rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-xs font-semibold text-slate-700 outline-none dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200">
                      <option value="">{all}</option>
                      {items.map((i) => <option key={i} value={i}>{i}</option>)}
                    </select>
                  ))}
                <button data-testid="se-filter-reset" onClick={() => setFilters({ service: '', source: '', priority: '' })}
                  className="rounded-lg px-2.5 py-1.5 text-xs font-bold text-blue-600 hover:underline">Reset</button>
              </div>
            )}
            <div className="min-w-0 overflow-x-auto">
              <table className="w-full min-w-[920px] border-separate border-spacing-0 text-xs">
                <thead>
                  <tr className="text-left text-[11px] font-bold uppercase tracking-wide text-slate-400">
                    {['Lead No', 'Client Name', 'Service', 'Source', 'Priority', 'Last Outcome', 'Last Contact',
                      'Next Action', 'Due In', 'Action'].map((h) => (
                        <th key={h} className="border-b border-slate-200 pb-2 pr-3 font-bold dark:border-slate-800">{h}</th>
                      ))}
                  </tr>
                </thead>
                <tbody data-testid="se-queue-body">
                  {visibleRows.map((r) => {
                    const [pLabel, pTone] = prioBadge(r.priority);
                    return (
                      <tr key={r.lead_id} data-testid={`se-queue-row-${r.lead_id}`}
                        className="group cursor-pointer transition-colors hover:bg-slate-50/80 dark:hover:bg-slate-800/50"
                        onClick={() => navigate(`/leads/${r.lead_id}`)}>
                        <td className="whitespace-nowrap border-b border-slate-100 py-2.5 pr-3 font-mono font-semibold text-primary dark:border-slate-800"
                          data-testid={`se-queue-leadno-${r.lead_id}`}>{leadNo(r)}</td>
                        <td className="max-w-[140px] truncate border-b border-slate-100 py-2.5 pr-3 font-bold text-slate-800 dark:border-slate-800 dark:text-slate-100">{r.client}</td>
                        <td className="max-w-[110px] truncate border-b border-slate-100 py-2.5 pr-3 text-slate-500 dark:border-slate-800">{r.service}</td>
                        <td className="max-w-[100px] truncate border-b border-slate-100 py-2.5 pr-3 text-slate-500 dark:border-slate-800">{r.source}</td>
                        <td className="border-b border-slate-100 py-2.5 pr-3 dark:border-slate-800">
                          <span className={`inline-block rounded px-1.5 py-0.5 text-[11px] font-medium ${pTone}`}>{pLabel}</span>
                        </td>
                        <td className={`whitespace-nowrap border-b border-slate-100 py-2.5 pr-3 font-semibold dark:border-slate-800 ${OUTCOME_TONE[r.last_outcome] || 'text-slate-500'}`}>
                          {r.last_outcome}
                        </td>
                        <td className="whitespace-nowrap border-b border-slate-100 py-2.5 pr-3 text-slate-500 dark:border-slate-800">
                          {r.last_contact ? dayLabel(r.last_contact) : '—'}
                        </td>
                        <td className="border-b border-slate-100 py-2.5 pr-3 dark:border-slate-800">
                          <span className="inline-flex items-center gap-1 rounded-md bg-slate-100 px-2 py-0.5 text-[10px] font-bold capitalize text-slate-600 dark:bg-slate-800 dark:text-slate-300">
                            {(r.next_action || 'call').replace(/_/g, ' ')}
                          </span>
                        </td>
                        <td className={`whitespace-nowrap border-b border-slate-100 py-2.5 pr-3 font-bold dark:border-slate-800 ${r.is_overdue ? 'text-rose-600' : 'text-slate-600 dark:text-slate-300'}`}>
                          {dueLabel(r)}
                        </td>
                        <td className="border-b border-slate-100 py-2.5 dark:border-slate-800">
                          <span className="flex items-center gap-1.5">
                            {!readOnly && (
                              <>
                                <ClickToCallButton lead={{ id: r.lead_id, primary_mobile: r.primary_mobile, whatsapp_number: r.whatsapp_number, customer_name: r.client }}
                                  leadId={r.lead_id} leadName={r.client} iconOnly stopPropagation
                                  dataTestid={`se-call-${r.lead_id}`}
                                  className="flex h-7 w-7 items-center justify-center rounded-lg bg-emerald-100 text-emerald-700 transition-colors hover:bg-emerald-200" />
                                <a data-testid={`se-whatsapp-${r.lead_id}`} onClick={(e) => e.stopPropagation()}
                                  href={`https://wa.me/${String(r.whatsapp_number || '').replace(/\D/g, '')}`}
                                  target="_blank" rel="noreferrer" title="WhatsApp"
                                  className="flex h-7 w-7 items-center justify-center rounded-lg bg-green-100 text-green-700 transition-colors hover:bg-green-200">
                                  <MessageCircle className="h-3.5 w-3.5" />
                                </a>
                              </>
                            )}
                            <button data-testid={`se-more-${r.lead_id}`} title="Open lead"
                              onClick={(e) => { e.stopPropagation(); navigate(`/leads/${r.lead_id}`); }}
                              className="flex h-7 w-7 items-center justify-center rounded-lg text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-600">
                              <MoreHorizontal className="h-4 w-4" />
                            </button>
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                  {!visibleRows.length && (
                    <tr><td colSpan={10} className="py-6 text-center text-slate-400" data-testid="se-queue-empty">
                      {loading ? 'Loading your queue…' : 'Nothing in this queue. Great work!'}
                    </td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </Panel>

          {untouchedAlert > 0 && (
            <div data-testid="se-untouched-alert"
              className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-rose-200 bg-[#FDEEF1] px-4 py-3 dark:border-rose-900/50 dark:bg-rose-950/20">
              <span className="flex min-w-0 items-center gap-2.5 text-sm font-semibold text-rose-700 dark:text-rose-300">
                <AlarmClock className="h-5 w-5 shrink-0 text-rose-500" />
                <span className="truncate">
                  {untouchedAlert} assigned open lead{untouchedAlert > 1 ? 's have' : ' has'} never been contacted
                  {oldestUntouched > 0 ? ` (oldest: ${oldestUntouched} day${oldestUntouched > 1 ? 's' : ''})` : ''}
                </span>
              </span>
              <button data-testid="se-view-fresh" onClick={() => { setTab('untouched'); setShowFilters(false); }}
                className="inline-flex shrink-0 items-center gap-1.5 rounded-xl border border-rose-200 bg-white px-3.5 py-2 text-xs font-bold text-rose-600 transition-colors hover:bg-rose-50">
                View Fresh Leads<ArrowRight className="h-3.5 w-3.5" />
              </button>
            </div>
          )}
        </div>

        <div className="min-w-0 space-y-3">
          <Panel testid="se-target" title="Today's Target" icon={Target} iconTone="text-emerald-600"
            action actionLabel="View Details" onAction={() => navigate('/targets')}>
            <div className="flex items-center gap-4">
              <Donut pct={T.overall_pct} />
              <ul className="min-w-0 flex-1 space-y-2.5">
                {(T.rows || []).map((r, i) => (
                  <li key={r.id} data-testid={`se-target-row-${r.target_type}`} className="min-w-0">
                    <span className="flex items-center justify-between gap-2 text-[11px]">
                      <span className="min-w-0 truncate font-semibold text-slate-600 dark:text-slate-300">{r.label}</span>
                      <span className="shrink-0 font-bold text-slate-900 dark:text-white">
                        {r.achieved == null ? 'Restricted'
                          : r.is_currency ? `${inr(r.achieved)} / ${inr(r.target)}` : `${r.achieved} / ${r.target}`}
                      </span>
                    </span>
                    <span className="mt-1 block h-1.5 w-full overflow-hidden rounded-full bg-slate-100 dark:bg-slate-800">
                      <span className={`block h-full rounded-full transition-all duration-700 ${TARGET_BARS[i % 3]}`}
                        style={{ width: `${Math.max(2, Math.min(r.pct, 100))}%` }} />
                    </span>
                  </li>
                ))}
                {!(T.rows || []).length && (
                  <li className="text-[11px] text-slate-400" data-testid="se-target-empty">No target assigned for this period.</li>
                )}
              </ul>
            </div>
            {T.stale && (T.rows || []).length > 0 && (
              <p className="mt-2 text-[10px] font-medium text-amber-600" data-testid="se-target-stale">
                Showing your most recent {T.period_type} target — no target covers today.
              </p>
            )}
            <div className="mt-3 grid grid-cols-1 gap-2.5 sm:grid-cols-[1fr_1.15fr]">
              <div className="rounded-xl border border-amber-200/80 bg-[#FEF8EC] p-3 dark:border-amber-900/40 dark:bg-amber-950/10"
                data-testid="se-remaining-today">
                <p className="text-[11px] font-bold text-slate-700 dark:text-slate-200">Remaining Today</p>
                <ul className="mt-2 space-y-1.5 text-[11px] font-semibold text-slate-600 dark:text-slate-300">
                  <li className="flex items-center gap-1.5"><Phone className="h-3.5 w-3.5 text-emerald-600" />
                    {T.remaining?.calls != null ? `${T.remaining.calls} Calls` : 'No call target'}</li>
                  <li className="flex items-center gap-1.5"><Users className="h-3.5 w-3.5 text-amber-600" />
                    {T.remaining?.qualified != null ? `${T.remaining.qualified} Qualified Leads` : 'No sales target'}</li>
                  <li className="flex items-center gap-1.5"><CalendarCheck className="h-3.5 w-3.5 text-rose-500" />
                    {T.remaining?.followups ?? 0} Follow-ups</li>
                </ul>
              </div>
              <div className={`flex flex-col gap-2 ${readOnly ? 'hidden' : ''}`}>
                <button data-testid="se-start-next-call" onClick={() => {
                  const next = visibleRows[0] || rows[0];
                  if (!next) { toast.info('Your queue is clear'); return; }
                  navigate(`/leads/${next.lead_id}`);
                }}
                  className="inline-flex items-center justify-center gap-2 rounded-xl bg-[#0E1626] px-3 py-2.5 text-xs font-bold text-white transition-transform hover:-translate-y-0.5">
                  <PhoneCall className="h-4 w-4" />Start Next Priority Call<ArrowRight className="h-3.5 w-3.5" />
                </button>
                <button data-testid="se-open-whatsapp" onClick={() => window.open('https://web.whatsapp.com', '_blank', 'noreferrer')}
                  className="inline-flex items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-xs font-bold text-slate-700 transition-colors hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200">
                  <MessageCircle className="h-4 w-4 text-green-600" />Open WhatsApp
                </button>
                <p className="text-center text-[10px] italic text-slate-400">Let's close more opportunities today!</p>
              </div>
            </div>
          </Panel>

          <Panel testid="se-incentive" title="This Month Incentive" icon={Trophy} iconTone="text-amber-500"
            action actionLabel="View Details" onAction={() => navigate('/my-business')}>
            <div className="flex items-center justify-between gap-3">
              <span className="flex min-w-0 items-center gap-3">
                <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-[#FEF6E7] text-[#B7761A]">
                  <Trophy className="h-6 w-6" />
                </span>
                <span data-testid="se-incentive-total" className="font-outfit text-[28px] font-extrabold leading-none text-emerald-600">
                  {I.total == null ? 'Restricted' : rupee(I.configured ? I.total : 0)}
                </span>
              </span>
              <ul className="shrink-0 space-y-1.5 text-[11px]">
                <li className="flex items-center justify-end gap-2">
                  <span className="h-2 w-2 rounded-full bg-emerald-500" />
                  <span className="text-slate-500">Confirmed</span>
                  <span data-testid="se-incentive-confirmed" className="font-bold text-slate-900 dark:text-white">{I.confirmed == null ? '—' : rupee(I.confirmed)}</span>
                </li>
                <li className="flex items-center justify-end gap-2">
                  <span className="h-2 w-2 rounded-full bg-amber-500" />
                  <span className="text-slate-500">Pending</span>
                  <span data-testid="se-incentive-pending" className="font-bold text-slate-900 dark:text-white">{I.pending == null ? '—' : rupee(I.pending)}</span>
                </li>
              </ul>
            </div>
            {!I.configured && I.total != null && (
              <p className="mt-2 text-[10px] font-medium text-amber-600" data-testid="se-incentive-unconfigured">
                Incentive rate is not configured yet — won business this month: {inr(I.won_value)}.
              </p>
            )}
          </Panel>
        </div>
      </div>

      {/* pipeline + schedule */}
      <div className="grid grid-cols-1 items-start gap-4 xl:grid-cols-[minmax(0,2.15fr)_minmax(0,1fr)]">
        <Panel testid="se-pipeline" icon={Filter} iconTone="text-blue-600"
          title={<span>My Pipeline <span className="font-medium text-slate-400">(Assigned Leads)</span></span>}
          action actionLabel="View Pipeline" onAction={() => navigate('/pipeline')}>
          <div className="grid grid-cols-2 gap-2.5 sm:grid-cols-4 lg:grid-cols-8">
            {(data?.pipeline || []).map((s, i) => (
              <button key={s.stage} data-testid={`se-pipeline-${s.stage}`} onClick={() => navigate(`/leads?status=${s.stage}`)}
                className={`rounded-xl px-2 py-3 text-center transition-transform hover:-translate-y-0.5 ${PIPE_TONE[i % PIPE_TONE.length]}`}>
                <span className="block truncate text-[11px] font-bold opacity-80">{s.label}</span>
                <span className="mt-1.5 block font-outfit text-xl font-extrabold leading-none">{s.count}</span>
                <span className="mt-1 block text-[10px] font-semibold opacity-70">{s.value == null ? '—' : inr(s.value)}</span>
              </button>
            ))}
          </div>
        </Panel>

        <Panel testid="se-schedule" title="Today's Schedule" icon={CalendarDays} iconTone="text-blue-600"
          action actionLabel="View Calendar" onAction={() => navigate('/calendar')}>
          <ul className="space-y-2">
            {(data?.schedule || []).map((s) => (
              <li key={s.id} data-testid={`se-schedule-${s.id}`}
                className="flex items-center gap-2.5 text-xs">
                <span className="h-2 w-2 shrink-0 rounded-full bg-rose-500" />
                <span className="w-[62px] shrink-0 font-bold text-slate-500">{clock(s.at)}</span>
                <span className="min-w-0 flex-1 truncate font-bold text-slate-800 dark:text-slate-100">{s.client}</span>
                <span className="hidden max-w-[90px] shrink-0 truncate text-slate-400 sm:block">{s.service}</span>
                <span className="shrink-0 rounded-md bg-slate-100 px-2 py-0.5 text-[10px] font-bold capitalize text-slate-600 dark:bg-slate-800 dark:text-slate-300">
                  {(s.type || 'call').replace(/_/g, ' ')}
                </span>
              </li>
            ))}
            {!(data?.schedule || []).length && (
              <li className="py-6 text-center text-xs text-slate-400" data-testid="se-schedule-empty">Nothing scheduled for today.</li>
            )}
          </ul>
        </Panel>
      </div>

      {/* fresh response + performance + activity */}
      <div className="grid grid-cols-1 items-start gap-4 lg:grid-cols-2 xl:grid-cols-3">
        <Panel testid="se-fresh-response" title={`Fresh Lead Response (${periodLabel})`} icon={Zap} iconTone="text-amber-500"
          action actionLabel="View Details" onAction={() => navigate('/call-intelligence')}>
          <div className="grid grid-cols-4 gap-2 divide-x divide-slate-100 dark:divide-slate-800">
            {[['new', F.new_leads ?? 0, 'New Leads', 'text-slate-900 dark:text-white'],
              ['contacted', F.contacted ?? 0, 'Contacted', 'text-emerald-600'],
              ['untouched', F.untouched ?? 0, 'Untouched', 'text-rose-600'],
              ['avg', F.avg_first_response_hms || '0s', 'Avg. First Response', 'text-slate-900 dark:text-white']].map(([k, v, l, tone]) => (
                <div key={k} data-testid={`se-fresh-${k}`} className="px-1 first:pl-0">
                  <p className={`font-outfit text-[22px] font-extrabold leading-none ${tone}`}>{v}</p>
                  <p className="mt-1.5 text-[10px] font-medium leading-tight text-slate-500">{l}</p>
                </div>
              ))}
          </div>
          <div className={`mt-3 flex items-center gap-2 rounded-xl border px-3 py-2.5 text-[11px] font-semibold ${(F.within_15_min_pct ?? 0) >= 60
            ? 'border-emerald-200 bg-emerald-50 text-emerald-700' : 'border-amber-200 bg-amber-50 text-amber-700'}`}
            data-testid="se-fresh-verdict">
            {(F.within_15_min_pct ?? 0) >= 60
              ? <CheckCircle2 className="h-4 w-4 shrink-0" /> : <AlertTriangle className="h-4 w-4 shrink-0" />}
            <span className="min-w-0">
              {F.contacted ? `${F.within_15_min_pct}% of contacted leads were reached within 15 minutes.`
                : 'No new lead has been contacted in this period yet.'}
            </span>
          </div>
        </Panel>

        <Panel testid="se-performance" icon={BarChart3} iconTone="text-blue-600"
          title={<span>Performance Overview <span className="font-medium text-slate-400 capitalize">({period === 'today' ? 'Today' : period})</span></span>}
          action actionLabel="View Report" onAction={() => navigate('/performance')}>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            {perfCells.map(([k, label, value, d, tone]) => (
              <div key={k} data-testid={`se-perf-${k}`}>
                <p className={`font-outfit text-[22px] font-extrabold leading-none ${tone}`}>{value}</p>
                <p className="mt-1.5 text-[10px] font-medium leading-tight text-slate-500">{label}</p>
                {d != null && (
                  <p className={`mt-1 flex items-center gap-0.5 text-[10px] font-bold ${d >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>
                    {d >= 0 ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}{Math.abs(d)}%
                  </p>
                )}
              </div>
            ))}
          </div>
        </Panel>

        <Panel testid="se-activity" title="Recent Activity" icon={FileText} iconTone="text-rose-500"
          action={canViewActivityLog} onAction={() => navigate('/activity')}>
          <ul className="space-y-2">
            {(data?.activity || []).map((a, i) => {
              const Icon = [PhoneCall, TrendingUp, FileText, MessageCircle, CalendarCheck, PhoneOff][i % 6];
              const tone = ['bg-[#EAF2FE] text-[#1B63D8]', 'bg-[#F1EDFD] text-[#6A3FD1]', 'bg-[#FEF6E7] text-[#B7761A]',
                'bg-[#E9F8EF] text-[#127A43]', 'bg-[#FDEEF3] text-[#C2185B]', 'bg-[#FDECEC] text-[#D32F2F]'][i % 6];
              return (
                <li key={a.id} data-testid={`se-activity-${a.id}`} className="flex items-center gap-2.5 text-xs">
                  <span className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-lg ${tone}`}>
                    <Icon className="h-3.5 w-3.5" />
                  </span>
                  <span className="w-[54px] shrink-0 font-semibold text-slate-400">{clock(a.at)}</span>
                  <span className="min-w-0 flex-1 truncate text-slate-700 dark:text-slate-200">{a.text}</span>
                  {a.client && <span className="hidden max-w-[90px] shrink-0 truncate font-semibold text-slate-500 sm:block">{a.client}</span>}
                </li>
              );
            })}
            {!(data?.activity || []).length && (
              <li className="py-6 text-center text-xs text-slate-400" data-testid="se-activity-empty">No recent activity.</li>
            )}
          </ul>
        </Panel>
      </div>
    </div>
  );
};

export default SalesExecutiveDashboardPage;
